import type { z } from 'zod';

import { asRecord, toString } from '@sites/entity-migrator';

import type { indentDefaultV0Schema } from '../default/v0';
import { createdAtOrNull, parseNumber, responsiveValue, stringOrUndefined } from '../../../common/migration';

type IndentDefaultV0Input = z.input<typeof indentDefaultV0Schema>;

/**
 * Lift a pre-versioning spacer to v0. `styles.height` becomes the `desktop` leaf
 * and `mobileStyles.height` the `mobile` one — absent where legacy had none, so
 * renderers keep falling back to the desktop height.
 *
 * Two legacy readings are resolved here, once, instead of by every consumer:
 * a `null` sync flag meant ON (the FormGroup panel seeded it `true`), and a
 * missing or `null` height rendered as 0 on the published page, so it becomes 0.
 */
export const migrateLegacyIndentToV0 = (input: unknown): IndentDefaultV0Input => {
    const legacy = asRecord(input);
    const data = asRecord(legacy['data']);
    const styles = asRecord(legacy['styles']);
    const mobile = asRecord(legacy['mobileStyles']);
    const isSynchronizeStyle = data['isSynchronizeStyle'];

    return {
        id: toString(legacy['id']),
        parentId: toString(legacy['structureId']) || toString(legacy['columnId']),
        entityType: 'widget:indent',
        entitySubType: 'default',
        version: 0,
        meta: {
            displayId: stringOrUndefined(legacy['displayId']) ?? null,
            createdAt: createdAtOrNull(legacy['createdAt']),
        },
        props: {
            syncWithDesktop: typeof isSynchronizeStyle === 'boolean' ? isSynchronizeStyle : true,
            height: responsiveValue(parseNumber(styles['height']) ?? 0, parseNumber(mobile['height'])),
        },
    };
};
